import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Trash2, Pencil, Search, X, Download, FileText } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export function AdminBookingsTab({ bookings, halls, onEdit, onDelete }) {
  const [filterHall, setFilterHall] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterMonth, setFilterMonth] = useState('');

  const filtered = useMemo(() => {
    return bookings.filter(b => {
      if (filterHall && b.hall_id !== filterHall) return false;
      if (filterUser) {
        const q = filterUser.toLowerCase();
        const name = (b.user_info?.nome_cognome || '').toLowerCase();
        const email = (b.user_info?.email || '').toLowerCase();
        const soc = (b.user_info?.societa_associazione || '').toLowerCase();
        if (!name.includes(q) && !email.includes(q) && !soc.includes(q)) return false;
      }
      if (filterMonth && b.start_datetime) {
        if (!b.start_datetime.startsWith(filterMonth)) return false;
      }
      return true;
    });
  }, [bookings, filterHall, filterUser, filterMonth]);

  const hasFilters = filterHall || filterUser || filterMonth;
  const clearFilters = () => { setFilterHall(''); setFilterUser(''); setFilterMonth(''); };

  const totalFiltered = filtered.reduce((sum, b) => sum + (b.total_price || 0), 0);

  const exportCSV = () => {
    const header = 'Sala;Utente;Email;Società;Data Inizio;Data Fine;Descrizione;Cucina;Totale';
    const rows = filtered.map(b => {
      const sala = b.hall_info?.name || '';
      const utente = b.user_info?.nome_cognome || '';
      const email = b.user_info?.email || '';
      const soc = b.user_info?.societa_associazione || '';
      const start = b.start_datetime ? format(parseISO(b.start_datetime), 'dd/MM/yyyy HH:mm') : '';
      const end = b.end_datetime ? format(parseISO(b.end_datetime), 'dd/MM/yyyy HH:mm') : '';
      const desc = (b.descrizione_manifestazione || '').replace(/;/g, ',').replace(/\n/g, ' ');
      const cucina = b.use_cucina ? 'Sì' : 'No';
      const tot = (b.total_price || 0).toFixed(2);
      return `${sala};${utente};${email};${soc};${start};${end};${desc};${cucina};${tot}`;
    });
    rows.push(`;;;;;;; TOTALE;${totalFiltered.toFixed(2)}`);
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prenotazioni_${filterMonth || 'tutte'}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportTXT = () => {
    let text = `RIEPILOGO PRENOTAZIONI\n${'='.repeat(60)}\n`;
    if (filterHall) { const h = halls.find(h => h.id === filterHall); text += `Sala: ${h?.name || filterHall}\n`; }
    if (filterMonth) text += `Periodo: ${filterMonth}\n`;
    if (filterUser) text += `Ricerca: ${filterUser}\n`;
    text += `Totale prenotazioni: ${filtered.length}\n${'='.repeat(60)}\n\n`;
    filtered.forEach((b, i) => {
      text += `${i + 1}. ${b.hall_info?.name || ''}\n`;
      text += `   Utente: ${b.user_info?.nome_cognome || ''}\n`;
      if (b.user_info?.societa_associazione) text += `   Società: ${b.user_info.societa_associazione}\n`;
      if (b.start_datetime && b.end_datetime) text += `   Dal ${format(parseISO(b.start_datetime), 'dd/MM/yyyy HH:mm')} al ${format(parseISO(b.end_datetime), 'dd/MM/yyyy HH:mm')}\n`;
      if (b.descrizione_manifestazione) text += `   Desc: ${b.descrizione_manifestazione}\n`;
      text += `   Totale: EUR ${(b.total_price || 0).toFixed(2)}\n\n`;
    });
    text += `${'='.repeat(60)}\nTOTALE: EUR ${totalFiltered.toFixed(2)}\n`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prenotazioni_${filterMonth || 'tutte'}_${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader><CardTitle className="font-heading text-2xl">Gestione Prenotazioni</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 p-4 rounded-lg border bg-muted/30" data-testid="booking-filters">
          <div className="flex-1 space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Sala</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              value={filterHall}
              onChange={(e) => setFilterHall(e.target.value)}
              data-testid="filter-hall"
            >
              <option value="">Tutte le sale</option>
              {halls.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Mese</label>
            <Input
              type="month"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="h-9"
              data-testid="filter-month"
            />
          </div>
          <div className="flex-[2] space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Utente</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca nome, email o società..."
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="h-9 pl-8"
                data-testid="filter-user"
              />
            </div>
          </div>
          {hasFilters && (
            <div className="flex items-end">
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9" data-testid="clear-filters">
                <X className="w-4 h-4 mr-1" /> Reset
              </Button>
            </div>
          )}
        </div>

        {/* Results count + Export */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span data-testid="booking-count">
            {filtered.length} prenotazion{filtered.length === 1 ? 'e' : 'i'}
            {hasFilters && ` (su ${bookings.length} totali)`}
          </span>
          <div className="flex items-center gap-2">
            {filtered.length > 0 && (
              <>
                <span className="font-semibold text-foreground" data-testid="booking-total-filtered">
                  Totale: &euro;{totalFiltered.toFixed(2)}
                </span>
                <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv">
                  <Download className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
                <Button variant="outline" size="sm" onClick={exportTXT} data-testid="export-txt">
                  <FileText className="w-3.5 h-3.5 mr-1" /> TXT
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            {hasFilters ? 'Nessuna prenotazione trovata con i filtri selezionati' : 'Nessuna prenotazione'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sala</TableHead><TableHead>Utente</TableHead><TableHead>Date</TableHead><TableHead>Servizi</TableHead><TableHead>Totale</TableHead><TableHead>Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((booking) => (
                  <TableRow key={booking.id} data-testid={`admin-booking-${booking.id}`}>
                    <TableCell className="font-medium">{booking.hall_info?.name}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{booking.user_info?.nome_cognome}</p>
                        {booking.user_info?.societa_associazione && <p className="text-xs font-medium text-primary">{booking.user_info.societa_associazione}</p>}
                        <p className="text-xs text-muted-foreground">{booking.user_info?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {booking.start_datetime && booking.end_datetime ? (
                        <div className="text-sm">
                          <p>{format(parseISO(booking.start_datetime), 'dd/MM/yyyy HH:mm')}</p>
                          <p>{format(parseISO(booking.end_datetime), 'dd/MM/yyyy HH:mm')}</p>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">Legacy</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {booking.use_cucina && <Badge variant="secondary" className="text-xs">Cucina</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold">&euro; {booking.total_price.toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => onEdit({...booking})} data-testid={`edit-booking-${booking.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm" data-testid={`delete-booking-${booking.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Conferma Cancellazione</AlertDialogTitle>
                              <AlertDialogDescription>Sei sicuro di voler cancellare questa prenotazione?</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annulla</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onDelete(booking.id)}>Cancella</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
